import {
  useRef,
  useState,
  useCallback,
  forwardRef,
  ChangeEvent,
  useLayoutEffect,
} from "react";
import { createTextMaskInputElement } from "text-mask-core";
import { mergeRefs } from "../common/utils/merge-refs";
import { IconPressEvent } from "../input/types";
import { Input } from "../index";
import { MaskInputProps, MaskInputElement } from "./types";
import { MASK_CHAR } from "./constants";

export const MaskInput = forwardRef<HTMLInputElement, MaskInputProps>(
  (
    {
      mask,
      value = "",
      placeholder,
      showMask = false,
      maskChar = MASK_CHAR,
      keepCharPositions = false,
      dataTestId = "mask-input",
      onClear,
      onChange,
      prepareChange,
      ...restProps
    },
    ref
  ) => {
    const textMask = useRef<MaskInputElement>();
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState("");

    const updateValue = useCallback((newValue = "") => {
      if (textMask.current && inputRef.current) {
        textMask.current.update(newValue);
        setInputValue(inputRef.current.value);
      }
    }, []);

    // useLayoutEffect необходим для мгновенного отображения отформатированного значения в поле ввода
    // в противном случае сначала отображается значение по умолчанию (пустая строка), а затем отформатированное значение, из-за чего лейбл "прыгает"
    useLayoutEffect(() => {
      if (inputRef.current) {
        textMask.current = createTextMaskInputElement({
          mask,
          guide: showMask,
          keepCharPositions,
          placeholderChar: maskChar,
          inputElement: inputRef.current,
          pipe: prepareChange,
          previousConformedValue: "",
          currentCaretPosition: 0,
          showMask: false,
          rawValue: "",
        });
      }
    }, [mask, showMask, maskChar, keepCharPositions, prepareChange]);

    useLayoutEffect(() => {
      updateValue(String(value));
    }, [updateValue, value]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        updateValue(event.target.value);
        onChange?.(event);
      },
      [updateValue, onChange]
    );

    const handleClear = useCallback(
      (event: IconPressEvent) => {
        if (onClear) {
          updateValue("");
          onClear(event);
        }
      },
      [updateValue, onClear]
    );

    return (
      <Input
        {...restProps}
        value={inputValue}
        onClear={handleClear}
        onChange={handleChange}
        dataTestId={dataTestId}
        ref={mergeRefs(ref, inputRef)}
        placeholder={!value ? placeholder : undefined}
      />
    );
  }
);
